import { createLibrary, createParser, defineComponent } from "@openuidev/lang-core";
import type { WorkspaceResult } from "@rakazo/contracts";
import { readWorkspaceResult, workspaceResultSourceIds } from "@rakazo/contracts";
import { z } from "zod";

const heading = z.string().min(1).max(160);
const evidenceIds = z.array(z.string().min(1).max(100)).max(32);
const SectionProps = z.object({ heading, body: z.string().min(1).max(4000), evidenceIds }).strict();
const RowProps = z.object({ cells: z.array(z.string().max(500)).min(2).max(5) }).strict();
const BarProps = z
  .object({
    label: z.string().min(1).max(100),
    value: z.string().regex(/^\d{1,12}(\.\d{1,6})?$/),
  })
  .strict();
const make = (name: string, description: string, props: z.ZodObject) =>
  defineComponent({ name, description, props, component: null });
const Section = make("Section", "A plain-text finding with source evidence IDs.", SectionProps);
const Row = make("Row", "One comparison row, in the declared column order.", RowProps);
const Bar = make("Bar", "A sourced nonnegative numeric observation.", BarProps);
const CompareProps = z
  .object({
    heading,
    columns: z.array(z.string().min(1).max(80)).min(2).max(5),
    rows: z.array(Row.ref).min(1).max(12),
    evidenceIds,
  })
  .strict();
const BarsProps = z
  .object({
    heading,
    unit: z.string().min(1).max(40),
    bars: z.array(Bar.ref).min(1).max(12),
    evidenceIds,
  })
  .strict();
const EvidenceProps = z.object({ evidenceId: z.string().min(1).max(100) }).strict();
const DecisionProps = z.object({ decisionId: z.string().min(1).max(100) }).strict();
const Compare = make("Compare", "A compact sourced comparison.", CompareProps);
const Bars = make("Bars", "A compact sourced bar chart with an explicit unit.", BarsProps);
const Evidence = make(
  "Evidence",
  "A view of an existing authorized evidence record.",
  EvidenceProps,
);
const Decision = make(
  "Decision",
  "An existing server decision, using its actual title and action.",
  DecisionProps,
);
const ReviewV1 = make(
  "Review",
  "A saved mission review.",
  z.object({ blocks: z.array(Section.ref).min(1).max(12) }).strict(),
);
const ReviewV2 = make(
  "Review",
  "A saved mission working surface.",
  z
    .object({
      blocks: z
        .array(z.union([Section.ref, Compare.ref, Bars.ref, Evidence.ref, Decision.ref]))
        .min(1)
        .max(12),
    })
    .strict(),
);
const libraries = [
  createLibrary({ components: [ReviewV1, Section], root: "Review" }),
  createLibrary({
    components: [ReviewV2, Section, Compare, Row, Bars, Bar, Evidence, Decision],
    root: "Review",
  }),
];
const parsers = libraries.map((library) => createParser(library.toJSONSchema(), library.root));
export type ReviewBlock =
  | ({ kind: "section" } & z.infer<typeof SectionProps>)
  | { kind: "compare"; heading: string; columns: string[]; rows: string[][]; evidenceIds: string[] }
  | {
      kind: "bars";
      heading: string;
      unit: string;
      bars: Array<{ label: string; value: number }>;
      evidenceIds: string[];
    }
  | ({ kind: "evidence" } & z.infer<typeof EvidenceProps>)
  | ({ kind: "decision" } & z.infer<typeof DecisionProps>);

/** Bound input before the permissive parser; quoted strings are always plain data. */
function boundedSource(source: string, version: number) {
  let syntax = "";
  let depth = 0,
    quoted = false,
    escaped = false;
  if (source.length > 32_000) throw new Error("Review exceeds its size limit.");
  for (const char of source) {
    if (!quoted && char !== '"') syntax += char;
    if (quoted) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === '"') quoted = false;
    } else if (char === '"') quoted = true;
    else if ("([{".includes(char)) {
      if (++depth > 12) throw new Error("Review nesting exceeds its limit.");
    } else if (")]}".includes(char)) {
      if (--depth < 0) throw new Error("Review syntax is incomplete.");
    }
  }
  if (quoted || depth !== 0) throw new Error("Review syntax is incomplete.");
  const allowed =
    version === 1
      ? ["root", "Review", "Section"]
      : version === 3
        ? ["root", "Review", "Section", "Compare", "Row", "Bars", "Bar"]
        : ["root", "Review", "Section", "Compare", "Row", "Bars", "Bar", "Evidence", "Decision"];
  const names = syntax.match(/[A-Za-z_$][A-Za-z0-9_$]*/g) ?? [];
  if (
    !/^\s*root\s*=\s*Review\s*\(/.test(syntax) ||
    names.filter((name) => name === "root").length !== 1 ||
    names.filter((name) => name === "Review").length !== 1 ||
    names.length > 160 ||
    names.some((name) => !allowed.includes(name)) ||
    /[^\s=(),[\]{};]/.test(syntax.replace(/[A-Za-z_$][A-Za-z0-9_$]*/g, ""))
  )
    throw new Error("Review uses an unsupported presentation.");
}
function node<T extends z.ZodType>(name: string, props: T) {
  return z
    .object({
      type: z.literal("element"),
      typeName: z.literal(name),
      props,
      partial: z.literal(false),
      hasDynamicProps: z.literal(false).optional(),
      statementId: z.string().optional(),
    })
    .strict();
}

export function parseWorkspaceResult(result: WorkspaceResult): ReviewBlock[] {
  boundedSource(result.presentation.source, result.version);
  const parsed = parsers[result.version === 1 ? 0 : 1]!.parse(result.presentation.source);
  if (
    parsed.root?.typeName !== "Review" ||
    parsed.root.partial ||
    parsed.root.hasDynamicProps ||
    parsed.rootExpression ||
    parsed.meta.errors.length ||
    parsed.meta.incomplete ||
    parsed.meta.unresolved.length ||
    parsed.meta.orphaned.length ||
    parsed.meta.statementCount > 160 ||
    parsed.queryStatements.length ||
    parsed.mutationStatements.length ||
    Object.keys(parsed.stateDeclarations).length
  )
    throw new Error("Review uses an unsupported or incomplete presentation.");
  const nodes = z.array(z.unknown()).min(1).max(12).parse(parsed.root.props.blocks);
  const allowed = new Set(workspaceResultSourceIds(result));
  const cite = (ids: string[]) => {
    if (ids.some((id) => !allowed.has(id))) throw new Error("Review cites an undeclared source.");
  };
  return nodes.map((value): ReviewBlock => {
    const name = z.object({ typeName: z.string() }).parse(value).typeName;
    if (name === "Section") {
      const props = node(name, SectionProps).parse(value).props;
      cite(props.evidenceIds);
      return { kind: "section", ...props };
    }
    if (result.version === 1) throw new Error("Review uses an unsupported presentation.");
    if (name === "Compare") {
      const props = node(
        name,
        CompareProps.extend({ rows: z.array(node("Row", RowProps)).min(1).max(12) }),
      ).parse(value).props;
      cite(props.evidenceIds);
      const rows = props.rows.map((row) => row.props.cells);
      if (rows.some((row) => row.length !== props.columns.length))
        throw new Error("Comparison columns do not match.");
      return { kind: "compare", ...props, rows };
    }
    if (name === "Bars") {
      const props = node(
        name,
        BarsProps.extend({ bars: z.array(node("Bar", BarProps)).min(1).max(12) }),
      ).parse(value).props;
      cite(props.evidenceIds);
      return {
        kind: "bars",
        ...props,
        bars: props.bars.map(({ props: bar }) => ({ label: bar.label, value: Number(bar.value) })),
      };
    }
    if (name === "Evidence" && result.version === 2) {
      const props = node(name, EvidenceProps).parse(value).props;
      cite([props.evidenceId]);
      return { kind: "evidence", ...props };
    }
    if (name === "Decision" && result.version === 2) {
      const props = node(name, DecisionProps).parse(value).props;
      if (!result.mission.decisionIds.includes(props.decisionId))
        throw new Error("Review cites an undeclared decision.");
      return { kind: "decision", ...props };
    }
    throw new Error("Review uses an unsupported presentation.");
  });
}

/** Invalid generated layouts retain their readable summary and cannot render action controls. */
export function workspaceResultView(text: string) {
  let result: WorkspaceResult;
  try {
    result = readWorkspaceResult(text);
  } catch {
    return null;
  }
  try {
    return { result, blocks: parseWorkspaceResult(result) };
  } catch {
    return { result, blocks: null };
  }
}

export * from "./conversation-handoff.js";
